/**
 * Thin client for the RSVP Worker.
 *
 * The Worker answers on the same origin in production (a Workers route sits in
 * front of Pages on /api/*) and is proxied by Vite in development, so every
 * call here is same-origin: no CORS, no bearer tokens, and the admin session
 * cookie rides along on its own without us ever touching it.
 *
 * The types come from the Worker package as `import type`, which is erased at
 * build time — nothing from `@wedding/api` reaches the browser bundle.
 */

import type { AdminList, Booking, Invite, LookupBody, LookupResult, RsvpBody } from '@wedding/api/types';

export type {
  AdminList,
  AdminParty,
  Booking,
  Invite,
  InviteGuest,
  LookupBody,
  LookupResult,
  RsvpBody,
} from '@wedding/api/types';

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

const OFFLINE = 'We could not reach the server. Check your connection and try again.';
const GENERIC = 'Something went wrong on our end. Please try again in a moment.';

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  let response: Response;
  try {
    response = await fetch(path, {
      ...init,
      headers: init.body ? { 'content-type': 'application/json', ...init.headers } : init.headers,
    });
  } catch (error) {
    // An aborted request is the caller unmounting, not a failure worth showing.
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    throw new ApiError(OFFLINE, 0);
  }

  if (response.ok) {
    if (response.status === 204) return undefined as T;
    return (await response.json()) as T;
  }

  // Surface the Worker's own wording when it gives some, never its internals.
  let detail = '';
  try {
    const body = (await response.json()) as { error?: unknown };
    if (typeof body.error === 'string') detail = body.error;
  } catch {
    /* non-JSON body — fall through */
  }
  throw new ApiError(detail || GENERIC, response.status);
}

/* ── Guests ────────────────────────────────────────────────────────────── */

/**
 * Finds an invitation by name and postcode, which is how every guest reaches
 * theirs.
 *
 * On a match the Worker sets a short-lived, HttpOnly party cookie and the
 * three calls below start working. Nothing identifying the party comes back to
 * this code — not a slug, not a guest id built from one — so there is nothing
 * here to keep hold of or to leak. Every failure is the same 404 with the same
 * sentence — a miss, an ambiguous name and a half-typed one are deliberately
 * indistinguishable, so do not try to tell the guest which one it was.
 */
export function lookupByName(body: LookupBody, signal?: AbortSignal): Promise<LookupResult> {
  return request<LookupResult>('/api/party/lookup', {
    method: 'POST',
    body: JSON.stringify(body),
    signal: signal ?? null,
  });
}

/** The invitation a name lookup opened. 401 once the pass has run out. */
export function fetchOwnInvite(signal?: AbortSignal): Promise<Invite> {
  return request<Invite>('/api/party', { signal: signal ?? null });
}

/** Sends a reply, or replaces an earlier one. Returns the invitation as saved. */
export function sendOwnRsvp(body: RsvpBody, signal?: AbortSignal): Promise<Invite> {
  return request<Invite>('/api/party/rsvp', {
    method: 'POST',
    body: JSON.stringify(body),
    signal: signal ?? null,
  });
}

/**
 * The room block's booking link.
 *
 * The URL is not in this bundle and not in any page — the Worker holds it and
 * only answers a request carrying a party pass, so a crawler reading the
 * travel page finds nothing to follow. Three answers matter to the caller:
 * a 200 with the link, a 401 meaning no pass yet (ask for the name), and a 404
 * meaning the hotel has not sent us the link at all.
 */
export function fetchBooking(signal?: AbortSignal): Promise<Booking> {
  return request<Booking>('/api/party/booking', { signal: signal ?? null });
}

/** Drops the party cookie — the "not you?" way out on a shared phone. */
export function forgetParty(): Promise<{ ok: true }> {
  return request<{ ok: true }>('/api/party', { method: 'DELETE' });
}

/* ── Admin ─────────────────────────────────────────────────────────────── */

export function adminLogin(key: string): Promise<{ ok: true }> {
  return request<{ ok: true }>('/api/admin/session', {
    method: 'POST',
    body: JSON.stringify({ key }),
  });
}

export function adminLogout(): Promise<{ ok: true }> {
  return request<{ ok: true }>('/api/admin/session', { method: 'DELETE' });
}

export function adminParties(signal?: AbortSignal): Promise<AdminList> {
  return request<AdminList>('/api/admin/parties', { signal: signal ?? null });
}

export const ADMIN_CSV_URL = '/api/admin/export.csv';
